import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";

interface GenerateSummaryButtonProps {
    mutationFn: () => Promise<{ id: number; summary: string }>;
    onSummaryGenerated?: (summaryId: number, summary: string) => void;
    variant?: "default" | "secondary" | "outlined";
    size?: "default" | "sm" | "md" | "lg" | "icon";
    className?: string;
}

export function GenerateSummaryButton({
    mutationFn,
    onSummaryGenerated,
    variant = "outlined",
    size = "sm",
    className,
}: GenerateSummaryButtonProps) {
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleGenerateSummary = async () => {
        setIsGenerating(true);
        setError(null);

        try {
            const response = await mutationFn();
            onSummaryGenerated?.(response.id, response.summary);
        } catch (err) {
            logger.error("Failed to generate summary:", err);
            setError("Failed to generate summary. Please try again.");
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="flex flex-col gap-1">
            <Button
                onClick={handleGenerateSummary}
                disabled={isGenerating}
                variant={variant}
                size={size}
                className={cn("gap-1.5", className)}
            >
                {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                <span>{isGenerating ? "Generating..." : "AI Summary"}</span>
            </Button>
            {error && <p className="text-xs text-red-400">{error}</p>}
        </div>
    );
}
